import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { PasswordUpdateStage, PasswordUpdateState } from '@/types/passwordUpdate/PasswordUpdate';

const initialState: PasswordUpdateState = {
  stage: 'idle',
  processedFileKeyCount: 0,
  totalFileKeyCount: 0,
  currentMessage: '',
  errors: [],
};

const passwordUpdateSlice = createSlice({
  name: 'passwordUpdate',
  initialState,
  reducers: {
    startUpdate(state) {
      state.stage = 'validatingOldPassword';
      state.processedFileKeyCount = 0;
      state.totalFileKeyCount = 0;
      state.currentMessage = 'Validating current password...';
      state.errors = [];
    },
    setStage(state, action: PayloadAction<{ stage: PasswordUpdateStage; message: string }>) {
      state.stage = action.payload.stage;
      state.currentMessage = action.payload.message;
    },
    setTotalFileKeys(state, action: PayloadAction<number>) {
      state.totalFileKeyCount = action.payload;
    },
    fileKeyReEncrypted(state) {
      state.processedFileKeyCount += 1;
      state.currentMessage =
        `Re-encrypting file keys... ${state.processedFileKeyCount}/${state.totalFileKeyCount}`;
    },
    fileKeyFailed(state, action: PayloadAction<string>) {
      state.errors.push(action.payload);
    },
    updateComplete(state) {
      state.stage = state.errors.length > 0 ? 'error' : 'complete';
      state.currentMessage = state.errors.length > 0
        ? `Completed with ${state.errors.length} error(s)`
        : 'Password updated successfully';
    },
    resetPasswordUpdate() {
      return initialState;
    },
  },
});

export const {
  startUpdate,
  setStage,
  setTotalFileKeys,
  fileKeyReEncrypted,
  fileKeyFailed,
  updateComplete,
  resetPasswordUpdate,
} = passwordUpdateSlice.actions;

/* -------------------------------------------------------------------------- */
/*  Selectors                                                                 */
/* -------------------------------------------------------------------------- */

export const selectPasswordUpdateStage = (state: { passwordUpdate: PasswordUpdateState }) =>
  state.passwordUpdate.stage;

export const selectPasswordUpdateProgress = (state: { passwordUpdate: PasswordUpdateState }) => ({
  processed: state.passwordUpdate.processedFileKeyCount,
  total: state.passwordUpdate.totalFileKeyCount,
});

export const selectPasswordUpdateMessage = (state: { passwordUpdate: PasswordUpdateState }) =>
  state.passwordUpdate.currentMessage;

export const selectPasswordUpdateErrors = (state: { passwordUpdate: PasswordUpdateState }) =>
  state.passwordUpdate.errors;

export const selectIsPasswordUpdateActive = (state: { passwordUpdate: PasswordUpdateState }) => {
  const { stage } = state.passwordUpdate;
  return stage !== 'idle' && stage !== 'complete' && stage !== 'error';
};

export default passwordUpdateSlice.reducer;
